import React, { useContext, useState } from "react";
import { GlobalContext } from "../../Context/GlobalContext";
import Calendar from "../../Components/Calender";
import { CalendarDays } from "lucide-react";
import { useLocation } from "react-router-dom";

const CreateCampaign = () => {
  const { translatedLabels, createCampaign } = useContext(GlobalContext);
  const screenLabels = translatedLabels?.common;
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [showCalendar, setShowCalendar] = useState(false);

  const location = useLocation();
  const editData = location.state?.campaignData;

  const [campaignData, setCampaignData] = useState(() => ({
    campaignName: editData?.name || "",
    maxSignUp: editData?.max_signups || "",
    perPersonLimit: editData?.per_user_limit || "",
    validTill: editData?.valid_till
      ? new Date(editData.valid_till)
      : new Date(),
  }));

  const formatTo12Hour = (date) => {
    if (!date) return "";

    const d = new Date(date);

    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();

    let hours = d.getHours();
    const minutes = String(d.getMinutes()).padStart(2, "0");

    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;

    return `${day}-${month}-${year} ${hours}:${minutes} ${ampm}`;
  };

  const handleInputChange = (field, value) => {
    setCampaignData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const addDuration = (amount, unit, label) => {
    const baseDate = new Date();

    if (unit === "minutes") {
      baseDate.setMinutes(baseDate.getMinutes() + amount);
    } else if (unit === "hours") {
      baseDate.setHours(baseDate.getHours() + amount);
    } else if (unit === "days") {
      baseDate.setDate(baseDate.getDate() + amount);
    }

    setSelectedPreset(label);

    handleInputChange("validTill", new Date(baseDate));
  };

  const isFormValid = () => {
    return (
      campaignData.campaignName.trim() &&
      campaignData.maxSignUp &&
      campaignData.perPersonLimit &&
      campaignData.validTill
    );
  };

  const formatToAPIDateTime = (date) => {
    if (!date) return "";

    const d = new Date(date);

    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");

    const hours = String(d.getHours()).padStart(2, "0");
    const minutes = String(d.getMinutes()).padStart(2, "0");
    const seconds = "00";

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!isFormValid()) return;

    const payload = {
      name: campaignData.campaignName,
      description: "",
      expiryDate: formatToAPIDateTime(campaignData.validTill),
      maxSignups: Number(campaignData.maxSignUp),
      maxScansPerUser: Number(campaignData.perPersonLimit),
    };

    if (isEditMode) {
      payload.campaignCode = editData?.code;
    }

    createCampaign(payload);
  };

  const isEditMode = !!editData;

  return (
    <div className="content-part bg-white">
      {isEditMode && (
        <div className="alert alert-warning py-1 px-2 mt-1 fs-15px">
          Campaign name and validity date cannot be changed.
        </div>
      )}
      <form onSubmit={handleSubmit}>
        <div className="form-group mb-3">
          <label className="form-label">
            {screenLabels?.create_campaign?.label_value || "Campaign Name"}{" "}
            {!campaignData.campaignName && (
              <span className="text-danger">*</span>
            )}
          </label>
          <input
            type="text"
            className="form-control wrInputText"
            value={campaignData.campaignName}
            disabled={isEditMode}
            placeholder={
              screenLabels?.enter_campaign_name?.label_value ||
              "Enter Campaign Name"
            }
            onChange={(e) => handleInputChange("campaignName", e.target.value)}
          />
        </div>

        <div className="form-group mb-3">
          <label className="form-label">
            {screenLabels?.max_signup?.label_value || "Maximum Signup"}{" "}
            {!campaignData.maxSignUp && <span className="text-danger">*</span>}
          </label>
          <input
            type="number"
            className="form-control mb-2"
            min="1"
            value={campaignData.maxSignUp}
            onChange={(e) => handleInputChange("maxSignUp", e.target.value)}
            placeholder={
              screenLabels?.enter_max_signup?.label_value ||
              "Enter maximum number of signups"
            }
          />
          <p className="text-muted mb-0 fs-13px">
            {screenLabels?.max_scan_note?.label_value ||
              "Number of people who can signup using this campaign QR code"}
          </p>
        </div>

        <div className="form-group mb-3">
          <label className="form-label">
            {screenLabels?.per_prsn_scan_limit?.label_value ||
              "Per Person Max Scan Limit"}{" "}
            {!campaignData.perPersonLimit && (
              <span className="text-danger">*</span>
            )}
          </label>
          <input
            type="number"
            className="form-control"
            min="1"
            value={campaignData.perPersonLimit}
            placeholder={
              screenLabels?.enter_scan_limit?.label_value ||
              "Enter per person scan limit"
            }
            onChange={(e) =>
              handleInputChange("perPersonLimit", e.target.value)
            }
          />
        </div>
        <div className="form-group mb-4 position-relative">
          <label className="form-label">
            Valid Till{" "}
            {!campaignData.validTill && <span className="text-danger">*</span>}
          </label>

          <div className="position-relative">
            <div
              className="form-control wrInputText pe-5"
              onClick={() => {
                if (!isEditMode) setShowCalendar(!showCalendar);
              }}
              style={{
                cursor: isEditMode ? "not-allowed" : "pointer",
                backgroundColor: isEditMode ? "#E9ECEF" : "#fff",
                opacity: isEditMode ? 0.6 : 1,
              }}
            >
              {campaignData.validTill
                ? formatTo12Hour(campaignData.validTill)
                : "Select date & time"}
            </div>

            <CalendarDays
              size={18}
              className="position-absolute top-50 end-0 translate-middle-y me-2 text-muted"
              style={{ pointerEvents: "none" }}
            />
          </div>

          {showCalendar && (
            <div
              className="position-absolute bg-white mt-1 p-2 rounded mb-3"
              style={{ zIndex: 1000 }}
            >
              <Calendar
                mode="campaign"
                value={campaignData.validTill}
                onDateChange={(dateObj) => {
                  handleInputChange("validTill", dateObj);
                  setSelectedPreset(null);
                }}
                onClose={() => setShowCalendar(false)}
              />
            </div>
          )}
          {!isEditMode && (
            <div className="preset-wrapper mt-2">
              <p className="fs-14px mb-2 text-muted">
                Or pick a quick validity option{" "}
              </p>

              <div className="d-flex flex-wrap gap-2">
                {[
                  { label: "1 Hour", value: 1, unit: "hours" },
                  { label: "3 Hours", value: 3, unit: "hours" },
                  { label: "6 Hours", value: 6, unit: "hours" },
                  { label: "12 Hours", value: 12, unit: "hours" },
                  { label: "1 Day", value: 1, unit: "days" },
                  { label: "7 Days", value: 7, unit: "days" },
                  { label: "30 Days", value: 30, unit: "days" },
                ].map((option) => (
                  <button
                    key={option.label}
                    type="button"
                    className={`validity-chip ${selectedPreset === option.label ? "active-chip" : ""
                      }`}
                    onClick={() =>
                      addDuration(option.value, option.unit, option.label)
                    }
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="text-center mb-4">
          <button type="submit" className="btn btn-primary">
            {isEditMode ? "Update Campaign" : "Create Campaign"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateCampaign;
